import React from 'react';
import { ArrowUpRight, Check, Grid3x3, Square, Undo2, X } from 'lucide-react';
import { arrowHead, type Pt, type Shot } from '@/lib/feedback/core';
import { loadImage } from './SnipOverlay';

/* SPEC-506 — "Mark it up": a box, an arrow, or a blur, drawn on a small canvas. No library.
 * Blur PIXELATES the area in the pixels themselves (not a CSS filter), so a customer name or
 * a price hidden here is really gone from the image that is sent. */

type Tool = 'box' | 'arrow' | 'blur';
interface Mark { tool: Tool; a: Pt; b: Pt }

const INK = '#f97316';

function drawMarks(ctx: CanvasRenderingContext2D, img: HTMLImageElement, marks: Mark[], w: number, h: number) {
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const line = Math.max(3, Math.round(w / 300));
  for (const m of marks) {
    const x = Math.min(m.a.x, m.b.x), y = Math.min(m.a.y, m.b.y);
    const rw = Math.abs(m.b.x - m.a.x), rh = Math.abs(m.b.y - m.a.y);
    if (m.tool === 'blur') {
      if (rw < 2 || rh < 2) continue;
      const block = Math.max(6, Math.round(w / 120));
      const tiny = document.createElement('canvas');
      tiny.width = Math.max(1, Math.round(rw / block));
      tiny.height = Math.max(1, Math.round(rh / block));
      const t = tiny.getContext('2d');
      if (!t) continue;
      t.drawImage(ctx.canvas, x, y, rw, rh, 0, 0, tiny.width, tiny.height);
      ctx.save();
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(tiny, 0, 0, tiny.width, tiny.height, x, y, rw, rh);
      ctx.restore();
    } else if (m.tool === 'box') {
      ctx.strokeStyle = INK;
      ctx.lineWidth = line;
      ctx.strokeRect(x, y, rw, rh);
    } else {
      ctx.strokeStyle = INK;
      ctx.fillStyle = INK;
      ctx.lineWidth = line;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(m.a.x, m.a.y);
      ctx.lineTo(m.b.x, m.b.y);
      ctx.stroke();
      const [p1, p2] = arrowHead(m.a, m.b, line * 5);
      ctx.beginPath();
      ctx.moveTo(m.b.x, m.b.y);
      ctx.lineTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.closePath();
      ctx.fill();
    }
  }
}

export function MarkupCanvas({ shot, onDone, onCancel }: {
  shot: Shot;
  onDone: (shot: Shot) => void;
  onCancel: () => void;
}) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [img, setImg] = React.useState<HTMLImageElement | null>(null);
  const [tool, setTool] = React.useState<Tool>('box');
  const [marks, setMarks] = React.useState<Mark[]>([]);
  const [draft, setDraft] = React.useState<Mark | null>(null);

  React.useEffect(() => {
    let live = true;
    loadImage(shot.url).then((i) => { if (live) setImg(i); }).catch(onCancel);
    return () => { live = false; };
  }, [shot.url, onCancel]);

  React.useEffect(() => {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx || !img) return;
    drawMarks(ctx, img, draft ? [...marks, draft] : marks, c.width, c.height);
  }, [img, marks, draft]);

  const toCanvas = (e: React.PointerEvent): Pt => {
    const c = canvasRef.current!;
    const box = c.getBoundingClientRect();
    return { x: ((e.clientX - box.left) / box.width) * c.width, y: ((e.clientY - box.top) / box.height) * c.height };
  };

  const done = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) { onCancel(); return; }
      onDone({ url: URL.createObjectURL(blob), width: c.width, height: c.height, source: shot.source });
    }, 'image/jpeg', 0.85);
  };

  const toolButton = (key: Tool, label: string, Icon: typeof Square) => (
    <button type="button" onClick={() => setTool(key)} aria-pressed={tool === key}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm ${tool === key ? 'bg-amber-500 text-[var(--text-inverse)]' : 'text-white border border-white/30 hover:bg-white/10'}`}>
      <Icon className="w-4 h-4" aria-hidden="true" /> {label}
    </button>
  );

  return (
    <div data-feedback-ui="" role="dialog" aria-modal="true" aria-label="Mark up the screenshot"
      className="fixed inset-0 z-[70] bg-black/85 flex flex-col items-center justify-center gap-3 p-6">
      <div className="flex flex-wrap items-center justify-center gap-2">
        {toolButton('box', 'Box', Square)}
        {toolButton('arrow', 'Arrow', ArrowUpRight)}
        {toolButton('blur', 'Blur', Grid3x3)}
        <button type="button" onClick={() => setMarks((m) => m.slice(0, -1))} disabled={!marks.length}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-white hover:bg-white/10 disabled:opacity-40">
          <Undo2 className="w-4 h-4" aria-hidden="true" /> Undo
        </button>
        <span className="w-px h-6 bg-white/20 mx-1" />
        <button type="button" onClick={done}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm bg-amber-500 text-[var(--text-inverse)] font-medium hover:bg-amber-400">
          <Check className="w-4 h-4" aria-hidden="true" /> Done
        </button>
        <button type="button" onClick={onCancel}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-gray-200 hover:bg-white/10">
          <X className="w-4 h-4" aria-hidden="true" /> Cancel
        </button>
      </div>
      <p className="text-xs text-gray-300">Blur hides what is under it in the picture itself — use it on names or prices you would rather not send.</p>
      <canvas
        ref={canvasRef}
        width={shot.width}
        height={shot.height}
        className="max-w-full max-h-[calc(100vh-140px)] rounded-md shadow-2xl cursor-crosshair touch-none"
        onPointerDown={(e) => { (e.target as Element).setPointerCapture?.(e.pointerId); const p = toCanvas(e); setDraft({ tool, a: p, b: p }); }}
        onPointerMove={(e) => { if (draft) setDraft({ ...draft, b: toCanvas(e) }); }}
        onPointerUp={() => { if (draft) { setMarks((m) => [...m, draft]); setDraft(null); } }}
        onPointerCancel={() => setDraft(null)}
      />
    </div>
  );
}
